import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import MaterialIcon from "./MaterialIcon";
import { browseProjects, filterProjects } from "../utils/projectMatch";

/**
 * ProjectPicker
 *
 * Type-ahead project/room field. On focus it shows a short browse list of
 * rooms (with `autoFocus`, only after the user clicks or types); typing
 * filters to matching names. The user can pick a match or keep
 * typing a brand-new name (the parent decides how to handle a new name, incl.
 * the "did you mean …?" re-confirm — see ProjectConfirmModal /
 * projectMatch.findCloseProject).
 *
 * Controlled: parent owns `value` (the typed text). `onSelectExisting` fires
 * when the user clicks a suggestion (parent typically sets value to its name
 * and remembers the id).
 *
 * Exact matches stay visible: typing a full short name like "Meck" must still
 * show "Meck" and longer hits (e.g. "Meck Schulcampus"), otherwise it looks
 * like search returned nothing.
 *
 * The suggestion menu is portaled to document.body so compose overflow
 * (`overflow: hidden` on the inline reply pane) cannot clip it.
 */
function ProjectPicker({
  value,
  onChange,
  onSelectExisting,
  buildings,
  placeholder,
  disabled,
  autoFocus,
  onEnter,
  strings = {},
}) {
  const [focused, setFocused] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  // An auto-focused field must not pop the full browse list over the modal
  // on open — it only opens once the user clicks, types or re-focuses.
  const [browseArmed, setBrowseArmed] = useState(!autoFocus);
  const blurTimer = useRef(null);
  const wrapRef = useRef(null);

  const hasQuery = !!String(value || "").trim();
  const suggestions = useMemo(() => {
    if (!hasQuery) return browseProjects(buildings, 12);
    return filterProjects(value, buildings, 12);
  }, [hasQuery, value, buildings]);
  const showList = focused && !disabled && suggestions.length > 0 && (hasQuery || browseArmed);

  const updateMenuPos = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({
      top: r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, 180),
    });
  };

  useLayoutEffect(() => {
    if (!showList) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
  }, [showList, value, suggestions.length]);

  useEffect(() => {
    if (!showList) return undefined;
    const onWin = () => updateMenuPos();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [showList]);

  const hasValue = !!(value && String(value).trim());

  const pick = (b) => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setFocused(false);
    if (onSelectExisting) onSelectExisting(b);
  };

  return (
    <div className="project-picker" ref={wrapRef}>
      <div className="project-picker-input-wrap">
        <input
          className={`email-classify-input project-picker-input${hasValue ? " project-picker-input--clearable" : ""}`}
          type="text"
          value={value}
          placeholder={placeholder || strings.email_project_name || "Project name"}
          onChange={(e) => {
            setBrowseArmed(true);
            onChange(e.target.value);
          }}
          onFocus={() => setFocused(true)}
          onMouseDown={() => setBrowseArmed(true)}
          onBlur={() => {
            // Delay so a suggestion click registers before the list unmounts.
            // Arm browse only after unfocusing — arming during the delay would
            // flash the list when an auto-focused field loses focus.
            blurTimer.current = setTimeout(() => {
              setFocused(false);
              setBrowseArmed(true);
            }, 150);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") setBrowseArmed(true);
            if (e.key === "Enter" && onEnter) {
              e.preventDefault();
              onEnter();
            }
          }}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
        />
        {hasValue && !disabled && (
          <button
            type="button"
            className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--round project-picker-clear"
            onMouseDown={(e) => {
              // Prevent input blur / suggestion race; clear in one click.
              e.preventDefault();
              if (blurTimer.current) clearTimeout(blurTimer.current);
              onChange("");
              setFocused(true);
            }}
            title={strings.clear || "Clear"}
            aria-label={strings.clear || "Clear"}
          >
            <MaterialIcon icon="close" size={16} />
          </button>
        )}
      </div>
      {showList && menuPos && ReactDOM.createPortal(
        <div
          className="project-picker-suggestions project-picker-suggestions--portal"
          style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
        >
          {suggestions.map((b) => (
            <div
              key={b.id}
              className="project-picker-suggestion"
              onMouseDown={(e) => {
                // onMouseDown (not onClick) so it fires before input blur.
                e.preventDefault();
                pick(b);
              }}
            >
              <MaterialIcon icon="folder" size={16} color="#e066ed" />
              <span>{b.name}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default ProjectPicker;
